import { type IFilter } from "@/interface/common.interface";
// import { logout } from '@/redux/features/UserSlice';

import React from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import moment from "moment-timezone";
import {
  BookOpen,
  Briefcase,
  Clock,
  FileCheck2,
  FilePlus,
  FileSignature,
  FileText,
  Scale,
  Lock,
  ShieldCheck,
} from "lucide-react";
import { API_URL } from "@/api/constant";

/**
 * common function to set cookies in react application
 * @param name name of cookie
 * @param value value to set in cookie
 */
export const setCookies = (name: string, value: string | object) => {
  Cookies.set(name, typeof value === "object" ? JSON.stringify(value) : value, {
    path: import.meta.env.VITE_PUBLIC_URL || "/",
  });
};

/**
 *
 * @param name find cookies by name
 * @returns
 */
export const getCookies = (name: string) => {
  const value = Cookies.get(name);
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/**
 *
 * @param name Delete cookies by name
 * @returns
 */
export const deleteCookies = (name: string) => {
  Cookies.remove(name, { path: import.meta.env.VITE_PUBLIC_URL || "/" });
};

/**
 *
 * @returns reutn the saved auth token
 */
export const getMyToken = () => {
  const value = Cookies.get(import.meta.env.VITE_BUSINESS + "token");
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export function toFixedThree(input: string | number, fixedTo = 3) {
  return parseFloat(input.toString()).toFixed(fixedTo);
}

// export const toFixedThree = (price: any) => {
//   const amount = parseFloat(price);
//   const formattedPrice = new Intl.NumberFormat('en-BH', {
//     style: 'currency',
//     currency: 'BHD',
//     minimumFractionDigits: 3,
//   }).format(amount);
//   return formattedPrice;
// };

/**
 *
 * @param type type of toast notification
 * @param message message to show in toast notification
 */

// import { deleteToken } from 'firebase/messaging';
// import { messaging } from '@/firebase-messaging';

export const logoutUser = async () => {
  deleteCookies(import.meta.env.VITE_BUSINESS + "token");
  deleteCookies(import.meta.env.VITE_BUSINESS + "role");
  deleteCookies(import.meta.env.VITE_BUSINESS + "refreshToken");
  localStorage.removeItem(`${import.meta.env.VITE_BUSINESS}fcmToken`);
  window.location.href = import.meta.env.VITE_PUBLIC_URL || "/";
  // setTimeout(() => {
  //     store.dispatch(logout());
  // }, 1000);
};

export const showToastNotification = (
  type: "success" | "danger" | "info" | "default" | "warning",
  message: string,
  options?: { id?: string; duration?: number },
) => {
  const commonOptions = {
    position: "top-right" as const,
    duration: options?.duration ?? 4000,
    id: options?.id || message,
  };

  // Create toast with close button
  const createToastWithClose = (
    toastFn: typeof toast | typeof toast.success | typeof toast.error,
    icon?: string,
  ) => {
    return toastFn(
      (t) =>
        React.createElement(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              gap: "8px",
            },
          },
          React.createElement("span", { style: { flex: 1 } }, message),
          React.createElement(
            "button",
            {
              onClick: () => toast.dismiss(t.id),
              style: {
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
                opacity: 0.7,
                padding: "0",
                marginLeft: "8px",
              },
            },
            "✕",
          ),
        ),
      { ...commonOptions, icon },
    );
  };

  switch (type) {
    case "success":
      return createToastWithClose(toast.success);

    case "danger":
      return createToastWithClose(toast.error);

    case "warning":
      return createToastWithClose(toast, "⚠️");

    case "info":
      return createToastWithClose(toast);

    case "default":
    default:
      return createToastWithClose(toast);
  }
};

let cachedDeviceId: string | null = null;

export const getDeviceId = (): string => {
  // Return the cached device ID if available
  if (cachedDeviceId !== null) {
    return cachedDeviceId;
  }

  // Attempt to retrieve an existing device ID from localStorage
  let deviceId = localStorage.getItem("deviceId");
  if (!deviceId) {
    // Use crypto.randomUUID() if available for secure UUID generation
    if (window.crypto && typeof crypto.randomUUID === "function") {
      deviceId = crypto.randomUUID();
    } else {
      // Fallback: simple UUID generator
      deviceId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        (c: string): string => {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        },
      );
    }
    // Save the generated ID so that it persists across sessions
    localStorage.setItem("deviceId", deviceId);
  }

  // Cache the ID in memory for subsequent call
  cachedDeviceId = deviceId;
  return deviceId;
};

export const statusColors = (status: string) => {
  switch (status?.toLowerCase()) {
    case "online":
    case "active":
      return "green-500";
    case "offline":
    case "inactive":
      return "red-500";
    case "created":
    case "pending":
      return "blue-500";
    default:
      return "gray-500";
  }
};

export const formatDateTime = (
  date: string | null | undefined | Date,
  type = "default",
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string => {
  if (!date) return "";

  const formattedDate: Record<string, string> = {
    default: moment.utc(date).tz(timezone).format("DD MMM YYYY, HH:mm"),
    YYMMDD: moment.utc(date).tz(timezone).format("DD MMM YYYY"),
  };

  return formattedDate[type] || "";
};

export const GetFilterUrl = (filter: IFilter) => {
  return Object.entries(filter)
    .filter(
      ([_, value]) =>
        value !== "" &&
        value !== "all" &&
        value !== undefined &&
        value !== null,
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
};

export const CleanFilterParams = (filter: IFilter): Record<string, any> => {
  return Object.entries(filter)
    .filter(
      ([_, value]) =>
        value !== "" &&
        value !== "all" &&
        value !== undefined &&
        value !== null,
    )
    .reduce(
      (acc, [key, value]) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<string, any>,
    );
};

export function formatStatus(status: string): string {
  return status
    .toLowerCase() // convert to lowercase
    .replace(/_/g, " ") // replace underscores with spaces
    .replace(/^\w/, (c) => c.toUpperCase()) // capitalize the first letter
    .replace(/\bof\b/g, "of"); // keep 'of' lowercase
}

// Helper function to format stat labels (camelCase to Title Case)
export const formatLabel = (key: string): string => {
  return key
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/_/g, " ") // replace underscores with spaces
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

export const formatRand = (
  value: number | string | null | undefined,
): string => {
  if (value === null || value === undefined || value === "") return "R0.00";

  const amount: number = typeof value === "string" ? Number(value) : value;

  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(isNaN(amount) ? 0 : amount);
};

export const firstLetterUpperCase = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn(
        "Clipboard API failed, falling back to legacy execution",
        err,
      );
    }
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed"; // Avoid scrolling
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.error("Fallback clipboard copy failed", err);
    return false;
  }
};

// export const downloadExcel = async (url: string, filename: string) => {
//   try {
//     const token = getMyToken();
//     const response = await fetch(url, {
//       method: "GET",
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//     });

//     if (!response.ok) {
//       throw new Error("Failed to download file");
//     }
//     const rawData = await response.arrayBuffer();
//     const blob = new Blob([rawData], {
//       type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//     });

//     const link = document.createElement("a");
//     link.href = URL.createObjectURL(blob);
//     link.download = filename;
//     link.click();

//     URL.revokeObjectURL(link.href);
//   } catch (error) {
//     console.error("Download error:", error);
//     showToastNotification("danger", "Failed to download Excel file");
//   }
// };

export const permissionKeys = {
  dashboard: "show",
  company: "co",
  attendance: "attendance",
  // extraWork: 'ew',
  leave: "leave",
  regularization: "ar",
  wfh: "wfh",
  payslip: "paySlip",
  holiday: "holiday",
  hrmSetting: "hs",
  assets: "asts",
  forms: "forms",
  show: "show",
  site: "site",
  branch: "branch",
  od: "od",
  organisation: "or",
  salaryHead: "salaryHead",
  onboard: "onboard",
  exitClearance: "exitClearance",
};

export const menuBars = [
  { label: "Company", value: permissionKeys.company },
  { label: "Attendance", value: permissionKeys.attendance },
  { label: "Assets", value: permissionKeys.assets },
  { label: "Leave", value: permissionKeys.leave },
  { label: "Att. Regularization", value: permissionKeys.regularization },
  { label: "WFH", value: permissionKeys.wfh },
  { label: "Payslip", value: permissionKeys.payslip },
  { label: "Holidays", value: permissionKeys.holiday },
  { label: "HRM Settings", value: permissionKeys.hrmSetting },
  { label: "Forms", value: permissionKeys.forms },
  { label: "Site", value: permissionKeys.site },
  { label: "Branch", value: permissionKeys.branch },
  { label: "Out Dore Duty", value: permissionKeys.od },
  { label: "Organisation", value: permissionKeys.organisation },
  { label: "Onboard", value: permissionKeys.onboard },
  { label: "Exit Clearance", value: permissionKeys.exitClearance },
];

export const formatDuration = (duration: string): string => {
  const map: Record<string, string> = {
    FULL_DAY: "Full Day",
    HALF_DAY: "Half Day",
    QUARTER_DAY: "Quarter Day",
  };
  return map[duration] || duration;
};

export const after =
  (compareWith: string | undefined, fieldName: string) =>
  (value: string | undefined) => {
    if (!value || !compareWith) return true;
    const isValid = value > compareWith;
    return isValid || `${fieldName} must be after ${compareWith}`;
  };

export const mapCategoryToType = (
  cat: string,
): "POLICY" | "TERMS_CONDITION" | "LETTER" => {
  if (cat === "policy") return "POLICY";
  if (cat === "terms") return "TERMS_CONDITION";
  return "LETTER";
};

// Extracts {variable} and [Insert Date] patterns
// export const extractVariables = (content: string): string[] => {
//   const matches = content.match(/\{[^}]+\}|\[[^\]]+\]/g);
//   if (!matches) return [];
//   return Array.from(new Set(matches));
// };

export const getIconForDocument = (
  title: string,
  category: string,
): React.ComponentType<{ className?: string }> => {
  const lower = title.toLowerCase();
  if (category === "policy") {
    if (lower.includes("leave") || lower.includes("attendance")) return Clock;
    if (
      lower.includes("it") ||
      lower.includes("security") ||
      lower.includes("data")
    )
      return ShieldCheck;
    if (lower.includes("posh") || lower.includes("harassment")) return Scale;
    return ShieldCheck;
  }
  if (category === "terms") {
    if (lower.includes("employment") || lower.includes("agreement"))
      return FileText;
    if (lower.includes("nda") || lower.includes("confidentiality")) return Lock;
    if (lower.includes("conduct") || lower.includes("ethics")) return BookOpen;
    return FileText;
  }
  if (category === "letter") {
    if (lower.includes("offer")) return FilePlus;
    if (lower.includes("appointment")) return FileCheck2;
    if (
      lower.includes("relieving") ||
      lower.includes("experience") ||
      lower.includes("exit")
    )
      return Briefcase;
    return FileSignature;
  }
  return FileText;
};

// download File
export const downloadFile = async (
  url: string,
  filename: string,
  contentType: string,
) => {
  try {
    const token = getMyToken();
    const fullUrl = url.startsWith("http") ? url : `${API_URL}${url}`;
    const response = await fetch(fullUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to download file");
    }

    const rawData = await response.arrayBuffer();
    const blob = new Blob([rawData], { type: contentType });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Download error:", error);
    showToastNotification("danger", `Failed to download ${filename}`);
  }
};

export const downloadExcel = async (url: string, filename: string) => {
  await downloadFile(
    url,
    filename,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  );
};

// download pdf
export const downloadPDF = async (url: string, filename: string) => {
  await downloadFile(url, filename, "application/pdf");
};

export const handleFormatAmount = (amount: number | string) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(Number(amount) || 0);
};

// fixed salary period (21st – 20th)
export const getCurrentPeriod = () => {
  const today = moment();
  const day = today.date();
  if (day <= 20) {
    return {
      startDate: today
        .clone()
        .subtract(1, "month")
        .date(21)
        .format("YYYY-MM-DD"),
      endDate: today.clone().date(20).format("YYYY-MM-DD"),
    };
  } else {
    return {
      startDate: today.clone().date(21).format("YYYY-MM-DD"),
      endDate: today.clone().add(1, "month").date(20).format("YYYY-MM-DD"),
    };
  }
};

export const generateMonthOptions = () => {
  const options: { label: string; startDate: string; endDate: string }[] = [];
  const today = moment();
  for (let i = 0; i < 12; i++) {
    const monthStart = today.clone().subtract(i, "months");
    const year = monthStart.year();
    const month = monthStart.month();
    const startMoment = moment([year, month, 21]);
    const endMoment = startMoment.clone().add(1, "month").date(20);
    const startDate = startMoment.format("YYYY-MM-DD");
    const endDate = endMoment.format("YYYY-MM-DD");
    const label =
      startMoment.format("MMM YYYY") + " – " + endMoment.format("MMM YYYY");
    options.push({ label, startDate, endDate });
  }
  return options;
};

export const getInitials = (name?: string) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const initials =
    parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
  return initials.toUpperCase();
};

export const getDaysLeftStyle = (daysLeft?: number | null) => {
  if (daysLeft === undefined || daysLeft === null) {
    return "bg-slate-50 text-slate-500";
  }
  if (daysLeft <= 3) return "bg-rose-50 text-rose-700";
  if (daysLeft <= 7) return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
};

export const getDaysLeftForDate = (dateStr: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diff = Math.ceil(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diff;
};

export const formatDate = (dateStr: string) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatTimeHM = (timeStr?: string): string => {
  if (!timeStr) return "—";
  const parts = timeStr.split(":");
  if (parts.length < 2) return timeStr;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return timeStr;
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
};

export const ALLOWED_PHOTO_PDF_EXTENSIONS = ["jpg", "jpeg", "png", "pdf"];
export const ALLOWED_PHOTO_PDF_ACCEPT =
  ".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf";

/**
 * Reusable file type validation helper with toast notification
 */
export const validateFileType = (
  file: File | null | undefined,
  allowedExtensions: string[] = ALLOWED_PHOTO_PDF_EXTENSIONS,
  showToast = true,
): boolean => {
  if (!file) return false;

  const fileExt = file.name.split(".").pop()?.toLowerCase();
  const mimeType = file.type?.toLowerCase();

  const isAllowedExt = Boolean(
    fileExt && allowedExtensions.map((e) => e.toLowerCase()).includes(fileExt),
  );
  const isAllowedMime =
    (allowedExtensions.includes("pdf") && mimeType === "application/pdf") ||
    ((allowedExtensions.includes("jpg") ||
      allowedExtensions.includes("jpeg")) &&
      mimeType === "image/jpeg") ||
    (allowedExtensions.includes("png") && mimeType === "image/png") ||
    (allowedExtensions.includes("webp") && mimeType === "image/webp");

  const isValid = isAllowedExt || isAllowedMime;

  if (!isValid && showToast) {
    const formattedExts = allowedExtensions
      .map((e) => e.toUpperCase())
      .join(" and ");
    showToastNotification(
      "danger",
      `Only ${formattedExts} formats are allowed.`,
    );
  }

  return isValid;
};

// Helper function to check if a designation is a Guest role
export const isGuestDesignation = (d?: {
  id?: string;
  name?: string;
  level?: number;
  aliasId?: string;
}) => {
  if (!d) return false;
  return (
    d.level === 5 ||
    d.name?.trim().toLowerCase().includes("guest") ||
    d.id?.trim().toLowerCase().includes("guest") ||
    d.aliasId?.trim().toLowerCase().includes("guest")
  );
};
